import { useEffect, useRef, useState } from 'preact/hooks';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectIsShowFeed,
  selectViewedStoreis,
  setIsShowFeed,
  setViewedStories,
} from '../../slice';
import FeedCircle from './FeedCircle';
import styles from './styles.module.scss';
import MoreButton from '../../../../../shared/MoreButton';
import close from '../../../../../public/img/close.svg';
import spinner from '../../../../../public/img/spinner.svg';

const Feed = () => {
  const [data, setData] = useState([]); // Tracks the data of the feed
  const [selected, setSelected] = useState(null); // Tracks the selected feed

  const [showNext, setShowNext] = useState(false); // Tracks if the next button should be shown
  const [showPrev, setShowPrev] = useState(false); // Tracks if the previous button should be shown

  const [showContextMenu, setShowContextMenu] = useState(false); // Tracks if the context menu should be shown

  const isShowFeed = useSelector(selectIsShowFeed); // Tracks if the feed should be shown
  const viewedStories = useSelector(selectViewedStoreis); // Tracks the viewed stories

  const containerRef = useRef(null) as any; // Ref for the feed container

  const dispatch = useDispatch();

  // Fetch the stories
  useEffect(() => {
    if (isShowFeed) {
      fetch(process.env.APP_URL + '/api/fb-videos')
        .then((res) => res.json())
        .then((data) => {
          // Put the viewed stories at the end
          const sorted = data
            .map(({ id, ...rest }) => ({
              ...rest,
              id,
              isViewed: viewedStories.includes(id),
            }))
            .sort((a, b) => a.isViewed - b.isViewed);

          setData(sorted);
        });
    }
  }, [isShowFeed]);

  //   // Resort the stories when the viewed stories change
  //   useEffect(() => {
  //     const sorted = data
  //       .map(({ id, ...rest }) => ({
  //         ...rest,
  //         id,
  //         isViewed: viewedStories.includes(id),
  //       }))
  //       .sort((a, b) => a.isViewed - b.isViewed);

  //     setData(sorted);
  //   }, [viewedStories]);

  // Scroll the feed to the right
  const goNext = () => {
    containerRef.current.scrollBy({ left: 330, behavior: 'smooth' });
  };

  // Scroll the feed to the left
  const goPrev = () => {
    containerRef.current.scrollBy({ left: -330, behavior: 'smooth' });
  };

  // Go to the next video in the feed
  const goNextVideo = () => {
    const index = data.findIndex((item) => item.id === selected.id);
    const next = data[index + 1] || data[0];
    next && setSelected(next);

    // Mark as viewed
    !viewedStories.includes(next.id) &&
      dispatch(setViewedStories([...viewedStories, next.id]));
  };

  // Go to the previous video in the feed
  const goPrevVideo = () => {
    const index = data.findIndex((item) => item.id === selected.id);
    const prev = data[index - 1] || data[data.length - 1];
    prev && setSelected(prev);

    // Mark as viewed
    !viewedStories.includes(prev.id) &&
      dispatch(setViewedStories([...viewedStories, prev.id]));
  };

  const formatCreatedTime = (created_time) => {
    const date = new Date(created_time);
    const options = { day: '2-digit', month: 'short' } as Intl.DateTimeFormatOptions;
    return date.toLocaleDateString('en-GB', options).replace(',', '');
  }

  // Add scroll event listener to the feed container
  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current as HTMLElement;
    const onScroll = () => {
      const scrollLeft = container.scrollLeft;
      const scrollWidth = container.scrollWidth;
      const clientWidth = container.clientWidth;

      setShowNext(scrollLeft + clientWidth < scrollWidth);
      setShowPrev(scrollLeft > 0);
    };

    container.addEventListener('scroll', onScroll);

    onScroll();

    return () => {
      container.removeEventListener('scroll', onScroll);
    };
  }, [containerRef.current]);

  useEffect(() => {
    const handleClick = (e) => {
      if (
        showContextMenu &&
        // @ts-ignore
        !document.getElementById('hideVideosFeedContextMenu').contains(e.target)
      ) {
        setShowContextMenu(false);
      }
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  });

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);

    // Timeout fallback in case onLoad is unreliable
    const timer = setTimeout(() => setLoading(false), 5000); // Max wait time 5s

    return () => clearTimeout(timer);
  }, [selected]);

  return (
    <>
      {isShowFeed && (
        <div className={styles.feedContainerWrapper}>
          <div
            className={styles.feedContainer}
            id="feedContainer"
            ref={containerRef}
          >
            {data.map((item) => (
              <FeedCircle
                timestamp={item.created_time}
                key={item.id}
                thumbnail={item.thumbnail}
                titleShort={formatCreatedTime(item.created_time) || '17 Feb'}
                isViewed={viewedStories.includes(item.id)}
                id={item.id}
                onClick={() => {
                  setSelected(item);
                  !viewedStories.includes(item.id) &&
                    dispatch(setViewedStories([...viewedStories, item.id]));
                }}
              />
            ))}
          </div>
          {showPrev && data?.length ? (
            <button onClick={goPrev} className={styles.navButton}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
                height={25}
                width={24}
                fill="#fff"
              >
                <path d="M512 256A256 256 0 1 0 0 256a256 256 0 1 0 512 0zM271 135c9.4-9.4 24.6-9.4 33.9 0s9.4 24.6 0 33.9l-87 87 87 87c9.4 9.4 9.4 24.6 0 33.9s-24.6 9.4-33.9 0L167 273c-9.4-9.4-9.4-24.6 0-33.9L271 135z" />
              </svg>
            </button>
          ) : (
            <></>
          )}

          {showNext && data?.length ? (
            <button
              onClick={goNext}
              className={`${styles.navButton} ${styles.navButtonNext}`}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 512 512"
                height={25}
                width={24}
                fill="#fff"
              >
                <path d="M0 256a256 256 0 1 0 512 0A256 256 0 1 0 0 256zM241 377c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l87-87-87-87c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0L345 239c9.4 9.4 9.4 24.6 0 33.9L241 377z" />
              </svg>
            </button>
          ) : (
            <></>
          )}

          <div className={`${styles.contextMenuContainer}`}>
            <MoreButton
              className={`${styles.contextMenuBtn}`}
              onClick={(): void => {
                setShowContextMenu((prev) => !prev);
              }}
            />

            {showContextMenu && (
              <div
                className={styles.contextMenuOverlay}
                id="hideVideosFeedContextMenu"
              >
                <div className={styles.contextMenu}>
                  <ul>
                    <li
                      onClick={() => {
                        dispatch(setIsShowFeed(false));
                        setShowContextMenu(false);
                      }}
                    >
                      <div className={styles.hideShells}>Hide videos feed</div>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {selected && (
        <div className={styles.feedPopup} onClick={() => setSelected(null)}>
          <div className={styles.videoContainer}>
            <div style={{ position: 'relative', height: '100%' }}>
              {loading && (
                <div
                  className={styles.placeholder}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <img src={spinner} alt="Spinner" />
                </div>
              )}
              <iframe
                key={selected.id}
                src={`https://www.facebook.com/plugins/video.php?href=https://www.facebook.com/watch/?v=${selected.id}`}
                scrolling="no"
                frameBorder="0"
                allow="autoplay; encrypted-media"
                allowTransparency={true}
                allowFullScreen={true}
                onLoad={() => setLoading(false)}
                style={{ height: '100%', boxShadow: 'none', display: 'block', position: 'relative', zIndex: 10 }}
              ></iframe>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                goPrevVideo();
              }}
              className={styles.navButtonPrev}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 320 512"
                height={28}
                width={28}
                fill="#fff"
              >
                <path d="M9.4 233.4c-12.5 12.5-12.5 32.8 0 45.3l192 192c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L77.3 256 246.6 86.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0l-192 192z" />
              </svg>
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                goNextVideo();
              }}
              className={styles.navButtonNext}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 320 512"
                height={28}
                width={28}
                fill="#fff"
              >
                <path d="M310.6 233.4c12.5 12.5 12.5 32.8 0 45.3l-192 192c-12.5 12.5-32.8 12.5-45.3 0s-12.5-32.8 0-45.3L242.7 256 73.4 86.6c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0l192 192z" />
              </svg>
            </button>
          </div>

          <button onClick={() => setSelected(null)}>
            <img src={close} alt="Close" />
          </button>
        </div>
      )}
    </>
  );
};

Feed.displayName = 'Feed';

export default Feed;
